import { Place as SiteIcon } from "@mui/icons-material";
import {
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import {
    ChainCard,
    ChainMaker,
    CountryCount,
    MakerSplit,
    RuleFlag,
} from "../../types/valueChain";
import { useChainActions } from "./chainActions";
import {
    FLAG_WORDS,
    SINGLE_MAKER,
    UNKNOWN_COUNTRY,
    singleMakerWords,
    useChainLens,
} from "./chainLens";

// A control on a card: pressing it neither drags the card nor pans the canvas
const CONTROL_CLASS = "nodrag nopan";

// The colour of the domestic makers: the share of the bar, the count, the
// home country's entry
export const DOMESTIC_COLOUR = "#2dd4bf";

// The colour of a domestic count of none
export const NONE_DOMESTIC_COLOUR = "#f5b84a";

// The border of a card the lens's rule flags
export const FLAGGED_BORDER = "rgba(245, 184, 74, 0.5)";

// The theme colour of the makers at home in a trusted country: a bucket of
// their own, never counted as domestic
export const TRUSTED_COLOUR = "info.main";

// The colour of the makers at home in another country, and the outline of
// those with no country on record
export const OTHER_COLOUR = "#767676";

// The domestic count's colour: teal, amber when there is none
export const domesticColour = (domestic: number): string =>
    domestic === 0 ? NONE_DOMESTIC_COLOUR : DOMESTIC_COLOUR;

// A number, in the monospace face
const Num: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box component="span" sx={monospaceStyle}>
        {children}
    </Box>
);

// What a maker without a production site on record shows instead: its own
// country, the weaker claim
export const WEAKER_CLAIM =
    "maker's home country; no production site on record";

// The bar of a card's makers by where they are at home: domestic, trusted,
// other, unknown, each as wide as its share
export const CountryBar: React.FC<{ split: MakerSplit }> = ({ split }) => {
    const total = split.domestic + split.trusted + split.other + split.unknown;
    const segments: {
        key: keyof MakerSplit;
        count: number;
        sx: Record<string, unknown>;
    }[] = [
        {
            key: "domestic",
            count: split.domestic,
            sx: { backgroundColor: DOMESTIC_COLOUR },
        },
        {
            key: "trusted",
            count: split.trusted,
            sx: { backgroundColor: TRUSTED_COLOUR },
        },
        {
            key: "other",
            count: split.other,
            sx: { backgroundColor: OTHER_COLOUR },
        },
        {
            key: "unknown",
            count: split.unknown,
            sx: {
                border: 1,
                borderColor: OTHER_COLOUR,
                borderRadius: "2px",
                boxSizing: "border-box",
            },
        },
    ];
    return (
        <Box
            role="img"
            aria-label={`Makers by country: ${split.domestic} domestic, ${split.trusted} trusted, ${split.other} other, ${split.unknown} unknown`}
            sx={{
                display: "flex",
                height: 6,
                gap: "1px",
                borderRadius: "3px",
                overflow: "hidden",
                backgroundColor: total === 0 ? "action.hover" : undefined,
            }}
        >
            {segments
                .filter((segment) => segment.count > 0)
                .map((segment) => (
                    <Box
                        key={segment.key}
                        data-segment={segment.key}
                        sx={{
                            flexGrow: segment.count,
                            flexBasis: 0,
                            ...segment.sx,
                        }}
                    />
                ))}
        </Box>
    );
};

// The size of a chip on a card or a report row
const SMALL_CHIP_SX = {
    height: 20,
    fontSize: "11.5px",
    alignSelf: "flex-start",
    maxWidth: "100%",
} as const;

// The flag chip of a card or a report row: amber, worded by its rule
export const FlagChip: React.FC<{ flag: RuleFlag; label?: string }> = ({
    flag,
    label,
}) => (
    <Chip
        label={label ?? FLAG_WORDS[flag]}
        size="small"
        color="warning"
        variant="outlined"
        sx={SMALL_CHIP_SX}
    />
);

// The chip of a card with exactly one maker on record, with that maker's
// country: shown under every lens, from the card's own flags
export const SingleMakerChip: React.FC<{ country: string | null }> = ({
    country,
}) => (
    <Chip
        label={singleMakerWords(country)}
        size="small"
        color="warning"
        variant="outlined"
        data-flag={SINGLE_MAKER}
        sx={SMALL_CHIP_SX}
    />
);

// The countries of a card with the home jurisdiction's entry first, the
// others in the response's order
export const homeFirst = (
    countries: readonly CountryCount[],
    home: string | null,
): CountryCount[] => {
    const isHome = (entry: CountryCount) =>
        home !== null && entry.country?.toUpperCase() === home;
    return [
        ...countries.filter(isHome),
        ...countries.filter((entry) => !isHome(entry)),
    ];
};

// A card's makers by country in one line, the home country first and in the
// domestic colour, a trusted one in its own, marked as trusted
const CountryLine: React.FC<{
    countries: readonly CountryCount[];
    home: string | null;
    trusted: readonly string[];
}> = ({ countries, home, trusted }) => (
    <Typography
        variant="caption"
        aria-label="Countries"
        sx={{ ...monospaceStyle, fontSize: 11, color: "text.secondary" }}
    >
        {homeFirst(countries, home).map((entry, index) => {
            const code = entry.country?.toUpperCase() ?? null;
            const bucket =
                code === null
                    ? "unknown"
                    : code === home
                      ? "domestic"
                      : trusted.includes(code)
                        ? "trusted"
                        : "other";
            return (
                <React.Fragment key={entry.country ?? UNKNOWN_COUNTRY}>
                    {index > 0 && " · "}
                    <Box
                        component="span"
                        data-bucket={bucket}
                        sx={{
                            color:
                                bucket === "domestic"
                                    ? DOMESTIC_COLOUR
                                    : bucket === "trusted"
                                      ? TRUSTED_COLOUR
                                      : undefined,
                        }}
                    >
                        {`${entry.country ?? UNKNOWN_COUNTRY} ${entry.count}`}
                        {bucket === "trusted" && " (trusted)"}
                    </Box>
                </React.Fragment>
            );
        })}
    </Typography>
);

// What the jurisdiction lens shows on a card: its makers with the domestic
// count in colour, the trusted count when a trusted set is named and the
// proven count, then the bar and the country line. The rule's flag sits in
// the card's footer. Nothing without a split: the card stays as under the
// makers lens.
export const JurisdictionBlock: React.FC<{
    card: ChainCard;
    root: boolean;
}> = ({ card, root }) => {
    const { home, trusted } = useChainLens();
    const split = card.maker_split;
    if (!split) {
        return null;
    }
    const proven = card.makers.filter(
        (maker) => maker.standing === "proven",
    ).length;
    return (
        <Box
            role="group"
            aria-label="Jurisdiction"
            sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
        >
            <Typography
                variant="caption"
                aria-label="Maker split"
                sx={{ color: "text.secondary" }}
            >
                <Num>{card.makers.length}</Num>{" "}
                {card.makers.length === 1 ? "maker" : "makers"}
                {root ? " on record" : ""}
                {" · "}
                <Box
                    component="span"
                    data-domestic="true"
                    sx={{
                        color: domesticColour(split.domestic),
                        fontWeight: 600,
                    }}
                >
                    <Num>{split.domestic}</Num> domestic
                </Box>
                {trusted.length > 0 && (
                    <>
                        {" · "}
                        <Box
                            component="span"
                            data-trusted="true"
                            sx={{ color: TRUSTED_COLOUR, fontWeight: 600 }}
                        >
                            <Num>{split.trusted}</Num> trusted
                        </Box>
                    </>
                )}
                {!root && (
                    <>
                        {" · "}
                        <Num>{proven}</Num> proven
                    </>
                )}
            </Typography>
            <CountryBar split={split} />
            {card.countries.length > 0 && (
                <CountryLine
                    countries={card.countries}
                    home={home}
                    trusted={trusted}
                />
            )}
        </Box>
    );
};

// The site pin of a maker whose product has a production facility on
// record: it opens the facility's view, or offers a choice of several
const SitePin: React.FC<{ maker: ChainMaker }> = ({ maker }) => {
    const { onOpenDetail } = useChainActions();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const makerName = maker.organization.name ?? maker.organization.id;
    const [first] = maker.facilities;
    const several = maker.facilities.length > 1;
    const siteName = (index: number): string => {
        const site = maker.facilities[index];
        return site.facility.name ?? site.facility.id;
    };
    return (
        <>
            <Tooltip
                title={
                    several
                        ? `${maker.facilities.length} production sites on record`
                        : `Production site: ${siteName(0)}${first.country ? ` (${first.country})` : ""}`
                }
            >
                <IconButton
                    size="small"
                    className={CONTROL_CLASS}
                    aria-label={
                        several
                            ? `Production sites of ${makerName}`
                            : `Production site of ${makerName}: ${siteName(0)}`
                    }
                    onClick={(event) => {
                        if (several) {
                            setAnchor(event.currentTarget);
                        } else {
                            onOpenDetail({
                                type: "facility",
                                id: first.facility.id,
                            });
                        }
                    }}
                    sx={{ p: 0.25, color: "link.main" }}
                >
                    <SiteIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            {several && (
                <Menu
                    anchorEl={anchor}
                    open={anchor !== null}
                    onClose={() => setAnchor(null)}
                >
                    {maker.facilities.map((site, index) => (
                        <MenuItem
                            key={site.facility.id}
                            onClick={() => {
                                setAnchor(null);
                                onOpenDetail({
                                    type: "facility",
                                    id: site.facility.id,
                                });
                            }}
                        >
                            {siteName(index)}
                            {site.country ? ` (${site.country})` : ""}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    );
};

// What a lens adds to one maker chip of a card: under the facilities lens
// its site pin, or its country marked as the weaker claim. The jurisdiction
// lens draws no maker chips; its country line stands for them.
export const MakerLensMark: React.FC<{ maker: ChainMaker }> = ({ maker }) => {
    const { lens } = useChainLens();
    const country = maker.country ?? UNKNOWN_COUNTRY;

    if (lens === "facilities") {
        if (maker.facilities.length > 0) {
            return <SitePin maker={maker} />;
        }
        return (
            <Tooltip title={WEAKER_CLAIM}>
                <Typography
                    variant="caption"
                    aria-label={`${country}: ${WEAKER_CLAIM}`}
                    data-claim="maker-country"
                    sx={{
                        ...monospaceStyle,
                        color: "text.secondary",
                        fontStyle: "italic",
                    }}
                >
                    {country} (maker's country)
                </Typography>
            </Tooltip>
        );
    }

    return null;
};
